import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { 
  Target, 
  Users, 
  TrendingUp, 
  FileCheck, 
  DollarSign,
  Save,
  Clock,
  CheckCircle,
  XCircle
} from 'lucide-react';

const SocialActorAssessment = ({ actor, onUpdate, canEdit, currentUser, onActorRefreshed }) => {
  const { t } = useTranslation();
  const [assessmentData, setAssessmentData] = useState({
    // Commitment & Strategy
    has_written_social_mission: actor?.organizational_indicators?.has_written_social_mission || false,
    social_impact_integrated_strategy: actor?.organizational_indicators?.social_impact_integrated_strategy || false,
    sdg_monitoring: actor?.organizational_indicators?.sdg_monitoring || false,
    impact_measurement_system: actor?.organizational_indicators?.impact_measurement_system || false,
    strategic_plan_years: actor?.organizational_indicators?.strategic_plan_years || '',
    
    // Governance
    board_meets_quarterly: actor?.organizational_indicators?.board_meets_quarterly || false,
    has_conflict_of_interest_policy: actor?.organizational_indicators?.has_conflict_of_interest_policy || false,
    financial_reports_frequency: actor?.organizational_indicators?.financial_reports_frequency || '',
    external_audit: actor?.organizational_indicators?.external_audit || false,
    stakeholder_engagement_mechanisms: actor?.organizational_indicators?.stakeholder_engagement_mechanisms || '',
    
    // Social Impact
    primary_impact_area: actor?.impact_indicators?.primary_impact_area || '',
    beneficiaries_reached_annually: actor?.impact_indicators?.beneficiaries_reached_annually || '',
    impact_assessment_conducted: actor?.impact_indicators?.impact_assessment_conducted || false,
    impact_assessment_year: actor?.impact_indicators?.impact_assessment_year || '',
    outcome_indicators: actor?.impact_indicators?.outcome_indicators || [],
    social_return_calculated: actor?.impact_indicators?.social_return_calculated || false,
    
    // Transparency & Accountability
    publishes_annual_report: actor?.organizational_indicators?.publishes_annual_report || false,
    financial_statements_public: actor?.organizational_indicators?.financial_statements_public || false,
    has_complaints_mechanism: actor?.organizational_indicators?.has_complaints_mechanism || false,
    certifications_held: actor?.organizational_indicators?.certifications_held || [],
    
    // Investment Readiness
    investment_ready: actor?.investment_readiness?.investment_ready || false,
    seeking_investment: actor?.investment_readiness?.seeking_investment || false,
    investment_amount_sought: actor?.investment_readiness?.investment_amount_sought || '',
    investment_type_preferred: actor?.investment_readiness?.investment_type_preferred || '',
    has_business_plan: actor?.investment_readiness?.has_business_plan || false,
    has_financial_projections: actor?.investment_readiness?.has_financial_projections || false,
  });

  const handleSave = async () => {
    try {
      // Organize data into appropriate categories
      const updatePayload = {
        organizational_indicators: {
          has_written_social_mission: assessmentData.has_written_social_mission,
          social_impact_integrated_strategy: assessmentData.social_impact_integrated_strategy,
          sdg_monitoring: assessmentData.sdg_monitoring,
          impact_measurement_system: assessmentData.impact_measurement_system,
          strategic_plan_years: assessmentData.strategic_plan_years,
          board_meets_quarterly: assessmentData.board_meets_quarterly,
          has_conflict_of_interest_policy: assessmentData.has_conflict_of_interest_policy,
          financial_reports_frequency: assessmentData.financial_reports_frequency,
          external_audit: assessmentData.external_audit,
          stakeholder_engagement_mechanisms: assessmentData.stakeholder_engagement_mechanisms,
          publishes_annual_report: assessmentData.publishes_annual_report,
          financial_statements_public: assessmentData.financial_statements_public,
          has_complaints_mechanism: assessmentData.has_complaints_mechanism,
          certifications_held: assessmentData.certifications_held,
        },
        impact_indicators: {
          primary_impact_area: assessmentData.primary_impact_area,
          beneficiaries_reached_annually: assessmentData.beneficiaries_reached_annually,
          impact_assessment_conducted: assessmentData.impact_assessment_conducted,
          impact_assessment_year: assessmentData.impact_assessment_year,
          outcome_indicators: assessmentData.outcome_indicators,
          social_return_calculated: assessmentData.social_return_calculated,
        },
        investment_readiness: {
          investment_ready: assessmentData.investment_ready,
          seeking_investment: assessmentData.seeking_investment,
          investment_amount_sought: assessmentData.investment_amount_sought,
          investment_type_preferred: assessmentData.investment_type_preferred,
          has_business_plan: assessmentData.has_business_plan,
          has_financial_projections: assessmentData.has_financial_projections,
        }
      };

      await onUpdate(updatePayload);
      toast.success(t('social_actor_assessment.toast_saved'));
    } catch (error) {
      console.error('Failed to save assessment:', error);
      toast.error(t('social_actor_assessment.toast_save_failed'));
    }
  };

  const isReviewer = currentUser?.role === 'administrator' || currentUser?.role === 'project_manager';
  const hasPending = !!(actor?.pending_assessment_scores && Object.keys(actor.pending_assessment_scores).length > 0);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);

  const refreshActor = async () => {
    if (typeof onActorRefreshed === 'function') {
      try { await onActorRefreshed(); } catch (e) { /* swallow */ }
    }
  };

  const handleApprovePending = async (updateCertification = false) => {
    if (!actor?.id) return;
    setReviewBusy(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/social-actors/${actor.id}/pending-assessment/approve?update_certification=${updateCertification ? 'true' : 'false'}`;
      const res = await fetch(url, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      const data = await res.json();
      if (res.ok) {
        toast.success(t('social_actor_assessment.toast_pending_approved', 'Pending self-assessment approved.'));
        await refreshActor();
      } else {
        toast.error(data?.detail || t('social_actor_assessment.toast_pending_approve_failed', 'Failed to approve pending assessment.'));
      }
    } catch (e) {
      console.error(e);
      toast.error(t('social_actor_assessment.toast_pending_approve_failed', 'Failed to approve pending assessment.'));
    } finally {
      setReviewBusy(false);
    }
  };

  const handleRejectPending = async () => {
    if (!actor?.id) return;
    setReviewBusy(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/social-actors/${actor.id}/pending-assessment/reject`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: rejectReason }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(t('social_actor_assessment.toast_pending_rejected', 'Pending self-assessment rejected.'));
        setShowRejectInput(false);
        setRejectReason('');
        await refreshActor();
      } else {
        toast.error(data?.detail || t('social_actor_assessment.toast_pending_reject_failed', 'Failed to reject pending assessment.'));
      }
    } catch (e) {
      console.error(e);
      toast.error(t('social_actor_assessment.toast_pending_reject_failed', 'Failed to reject pending assessment.'));
    } finally {
      setReviewBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Pending self-assessment banner */}
      {hasPending && (
        <Card className="border-amber-300 bg-amber-50" data-testid="pending-assessment-banner">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-amber-900 text-base">
              <Clock className="h-5 w-5" />
              {t('social_actor_assessment.pending_title', 'Self-assessment pending review')}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-sm text-amber-900">
              {isReviewer
                ? t('social_actor_assessment.pending_subtitle_reviewer', "This organisation submitted a self-assessment. Approve to publish the new scores; the certification level only changes if you choose to update it.")
                : t('social_actor_assessment.pending_subtitle_owner', "Your newly calculated score is pending review by an Administrator or Project Manager. Your organisation's published score and certification level will remain unchanged until approved.")}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-3">
              {['overall','strategy','governance','impact','transparency','investment'].map((k) => (
                <div key={k} className="bg-white border rounded-md py-2 text-center">
                  <div className="text-lg font-bold text-amber-700">{actor.pending_assessment_scores[k] ?? 0}%</div>
                  <div className="text-xs uppercase tracking-wide text-gray-500">{t(`social_actor_assessment.score_${k}`)}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center justify-between mt-3 gap-3">
              <p className="text-xs text-amber-900">
                {actor.pending_assessment_at && (
                  <>{t('social_actor_assessment.pending_submitted_at', 'Submitted: {{date}}', { date: new Date(actor.pending_assessment_at).toLocaleString('en-GB') })}</>
                )}
                {actor.pending_recommended_certification && (
                  <> · {t('social_actor_assessment.pending_recommended', 'Recommended level: {{lvl}}', { lvl: String(actor.pending_recommended_certification).replace(/_/g, ' ') })}</>
                )}
              </p>
              {isReviewer && (
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    disabled={reviewBusy}
                    onClick={() => handleApprovePending(false)}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white"
                    data-testid="approve-pending-assessment-btn"
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    {t('social_actor_assessment.approve_btn', 'Approve scores')}
                  </Button>
                  {actor.pending_recommended_certification && actor.pending_recommended_certification !== actor.certification_level && (
                    <Button
                      size="sm"
                      disabled={reviewBusy}
                      onClick={() => handleApprovePending(true)}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="approve-pending-with-cert-btn"
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      {t('social_actor_assessment.approve_with_cert_btn', 'Approve & update level')}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={reviewBusy}
                    onClick={() => setShowRejectInput((v) => !v)}
                    className="border-red-300 text-red-700 hover:bg-red-50"
                    data-testid="reject-pending-assessment-btn"
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    {t('social_actor_assessment.reject_btn', 'Reject')}
                  </Button>
                </div>
              )}
            </div>
            {isReviewer && showRejectInput && (
              <div className="mt-3 flex gap-2">
                <Input
                  placeholder={t('social_actor_assessment.reject_reason_placeholder', 'Optional reason for rejection')}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  data-testid="reject-reason-input"
                />
                <Button size="sm" variant="destructive" disabled={reviewBusy} onClick={handleRejectPending} data-testid="confirm-reject-pending-btn">
                  {t('social_actor_assessment.confirm_reject_btn', 'Confirm reject')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Assessment Scores Display */}
      {actor?.assessment_scores && Object.keys(actor.assessment_scores).length > 0 && (
        <Card className="bg-gradient-to-r from-blue-50 to-purple-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              {t('social_actor_assessment.scores_title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">
                  {actor.assessment_scores.overall || 0}%
                </div>
                <div className="text-sm text-gray-600 mt-1">{t('social_actor_assessment.score_overall')}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-semibold text-green-700">
                  {actor.assessment_scores.strategy || 0}%
                </div>
                <div className="text-xs text-gray-600 mt-1">{t('social_actor_assessment.score_strategy')}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-semibold text-purple-600">
                  {actor.assessment_scores.governance || 0}%
                </div>
                <div className="text-xs text-gray-600 mt-1">{t('social_actor_assessment.score_governance')}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-semibold text-orange-700">
                  {actor.assessment_scores.impact || 0}%
                </div>
                <div className="text-xs text-gray-600 mt-1">{t('social_actor_assessment.score_impact')}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-semibold text-teal-700">
                  {actor.assessment_scores.transparency || 0}%
                </div>
                <div className="text-xs text-gray-600 mt-1">{t('social_actor_assessment.score_transparency')}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-semibold text-indigo-600">
                  {actor.assessment_scores.investment || 0}%
                </div>
                <div className="text-xs text-gray-600 mt-1">{t('social_actor_assessment.score_investment')}</div>
              </div>
            </div>
            {actor.last_assessment_date && (
              <div className="text-xs text-gray-500 mt-4 text-center">
                {t('social_actor_assessment.last_calculated', { date: new Date(actor.last_assessment_date).toLocaleDateString('en-GB') })}
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      <Tabs defaultValue="strategy" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="strategy">
            <Target className="h-4 w-4 mr-2" />
            {t('social_actor_assessment.tab_strategy')}
          </TabsTrigger>
          <TabsTrigger value="governance">
            <Users className="h-4 w-4 mr-2" />
            {t('social_actor_assessment.tab_governance')}
          </TabsTrigger>
          <TabsTrigger value="impact">
            <TrendingUp className="h-4 w-4 mr-2" />
            {t('social_actor_assessment.tab_impact')}
          </TabsTrigger>
          <TabsTrigger value="transparency">
            <FileCheck className="h-4 w-4 mr-2" />
            {t('social_actor_assessment.tab_transparency')}
          </TabsTrigger>
          <TabsTrigger value="investment">
            <DollarSign className="h-4 w-4 mr-2" />
            {t('social_actor_assessment.tab_investment')}
          </TabsTrigger>
        </TabsList>

        {/* Commitment & Strategy Tab */}
        <TabsContent value="strategy" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('social_actor_assessment.card_strategy_title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="has_written_social_mission"
                  checked={assessmentData.has_written_social_mission}
                  onChange={(e) => setAssessmentData({...assessmentData, has_written_social_mission: e.target.checked})}
                  disabled={!canEdit}
                  className="h-4 w-4"
                />
                <Label htmlFor="has_written_social_mission">{t('social_actor_assessment.field_has_written_mission')}</Label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="social_impact_integrated_strategy"
                  checked={assessmentData.social_impact_integrated_strategy}
                  onChange={(e) => setAssessmentData({...assessmentData, social_impact_integrated_strategy: e.target.checked})}
                  disabled={!canEdit}
                  className="h-4 w-4"
                />
                <Label htmlFor="social_impact_integrated_strategy">{t('social_actor_assessment.field_strategy_integrated')}</Label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="sdg_monitoring"
                  checked={assessmentData.sdg_monitoring}
                  onChange={(e) => setAssessmentData({...assessmentData, sdg_monitoring: e.target.checked})}
                  disabled={!canEdit}
                  className="h-4 w-4"
                />
                <Label htmlFor="sdg_monitoring">{t('social_actor_assessment.field_sdg_monitoring')}</Label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="impact_measurement_system"
                  checked={assessmentData.impact_measurement_system}
                  onChange={(e) => setAssessmentData({...assessmentData, impact_measurement_system: e.target.checked})}
                  disabled={!canEdit}
                  className="h-4 w-4"
                />
                <Label htmlFor="impact_measurement_system">{t('social_actor_assessment.field_impact_measurement')}</Label>
              </div>

              <div>
                <Label htmlFor="strategic_plan_years">{t('social_actor_assessment.field_strategic_plan_years')}</Label>
                <Input
                  id="strategic_plan_years"
                  type="number"
                  value={assessmentData.strategic_plan_years}
                  onChange={(e) => setAssessmentData({...assessmentData, strategic_plan_years: e.target.value})}
                  disabled={!canEdit}
                  placeholder={t('social_actor_assessment.field_strategic_plan_years_placeholder')}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Governance Tab */}
        <TabsContent value="governance" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('social_actor_assessment.card_governance_title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="board_meets_quarterly"
                  checked={assessmentData.board_meets_quarterly}
                  onChange={(e) => setAssessmentData({...assessmentData, board_meets_quarterly: e.target.checked})}
                  disabled={!canEdit}
                  className="h-4 w-4"
                />
                <Label htmlFor="board_meets_quarterly">{t('social_actor_assessment.field_board_quarterly')}</Label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="has_conflict_of_interest_policy"
                  checked={assessmentData.has_conflict_of_interest_policy}
                  onChange={(e) => setAssessmentData({...assessmentData, has_conflict_of_interest_policy: e.target.checked})}
                  disabled={!canEdit}
                  className="h-4 w-4"
                />
                <Label htmlFor="has_conflict_of_interest_policy">{t('social_actor_assessment.field_conflict_policy')}</Label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="external_audit"
                  checked={assessmentData.external_audit}
                  onChange={(e) => setAssessmentData({...assessmentData, external_audit: e.target.checked})}
                  disabled={!canEdit}
                  className="h-4 w-4"
                />
                <Label htmlFor="external_audit">{t('social_actor_assessment.field_external_audit')}</Label>
              </div>

              <div>
                <Label htmlFor="financial_reports_frequency">{t('social_actor_assessment.field_reports_freq')}</Label>
                <Select
                  value={assessmentData.financial_reports_frequency}
                  onValueChange={(value) => setAssessmentData({...assessmentData, financial_reports_frequency: value})}
                  disabled={!canEdit}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('social_actor_assessment.field_reports_freq_placeholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">{t('social_actor_assessment.freq_monthly')}</SelectItem>
                    <SelectItem value="quarterly">{t('social_actor_assessment.freq_quarterly')}</SelectItem>
                    <SelectItem value="semi-annually">{t('social_actor_assessment.freq_semi')}</SelectItem>
                    <SelectItem value="annually">{t('social_actor_assessment.freq_annually')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="stakeholder_engagement_mechanisms">{t('social_actor_assessment.field_stakeholder')}</Label>
                <Textarea
                  id="stakeholder_engagement_mechanisms"
                  value={assessmentData.stakeholder_engagement_mechanisms}
                  onChange={(e) => setAssessmentData({...assessmentData, stakeholder_engagement_mechanisms: e.target.value})}
                  disabled={!canEdit}
                  rows={3}
                  placeholder={t('social_actor_assessment.field_stakeholder_placeholder')}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Social Impact Tab */}
        <TabsContent value="impact" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('social_actor_assessment.card_impact_title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="primary_impact_area">{t('social_actor_assessment.field_primary_impact')}</Label>
                <Select
                  value={assessmentData.primary_impact_area}
                  onValueChange={(value) => setAssessmentData({...assessmentData, primary_impact_area: value})}
                  disabled={!canEdit}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('social_actor_assessment.field_primary_impact_placeholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="education">{t('social_actor_assessment.area_education')}</SelectItem>
                    <SelectItem value="employment">{t('social_actor_assessment.area_employment')}</SelectItem>
                    <SelectItem value="health">{t('social_actor_assessment.area_health')}</SelectItem>
                    <SelectItem value="environment">{t('social_actor_assessment.area_environment')}</SelectItem>
                    <SelectItem value="community">{t('social_actor_assessment.area_community')}</SelectItem>
                    <SelectItem value="poverty">{t('social_actor_assessment.area_poverty')}</SelectItem>
                    <SelectItem value="inclusion">{t('social_actor_assessment.area_inclusion')}</SelectItem>
                    <SelectItem value="other">{t('social_actor_assessment.area_other')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="beneficiaries_reached_annually">{t('social_actor_assessment.field_beneficiaries_annual')}</Label>
                <Input
                  id="beneficiaries_reached_annually"
                  type="number"
                  value={assessmentData.beneficiaries_reached_annually}
                  onChange={(e) => setAssessmentData({...assessmentData, beneficiaries_reached_annually: e.target.value})}
                  disabled={!canEdit}
                  placeholder={t('social_actor_assessment.field_beneficiaries_annual_placeholder')}
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="impact_assessment_conducted"
                  checked={assessmentData.impact_assessment_conducted}
                  onChange={(e) => setAssessmentData({...assessmentData, impact_assessment_conducted: e.target.checked})}
                  disabled={!canEdit}
                  className="h-4 w-4"
                />
                <Label htmlFor="impact_assessment_conducted">{t('social_actor_assessment.field_assessment_conducted')}</Label>
              </div>

              {assessmentData.impact_assessment_conducted && (
                <div>
                  <Label htmlFor="impact_assessment_year">{t('social_actor_assessment.field_assessment_year')}</Label>
                  <Input
                    id="impact_assessment_year"
                    type="number"
                    value={assessmentData.impact_assessment_year}
                    onChange={(e) => setAssessmentData({...assessmentData, impact_assessment_year: e.target.value})}
                    disabled={!canEdit}
                    placeholder={t('social_actor_assessment.field_assessment_year_placeholder')}
                  />
                </div>
              )}

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="social_return_calculated"
                  checked={assessmentData.social_return_calculated}
                  onChange={(e) => setAssessmentData({...assessmentData, social_return_calculated: e.target.checked})}
                  disabled={!canEdit}
                  className="h-4 w-4"
                />
                <Label htmlFor="social_return_calculated">{t('social_actor_assessment.field_sroi_calculated')}</Label>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transparency Tab */}
        <TabsContent value="transparency" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('social_actor_assessment.card_transparency_title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="publishes_annual_report"
                  checked={assessmentData.publishes_annual_report}
                  onChange={(e) => setAssessmentData({...assessmentData, publishes_annual_report: e.target.checked})}
                  disabled={!canEdit}
                  className="h-4 w-4"
                />
                <Label htmlFor="publishes_annual_report">{t('social_actor_assessment.field_publishes_report')}</Label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="financial_statements_public"
                  checked={assessmentData.financial_statements_public}
                  onChange={(e) => setAssessmentData({...assessmentData, financial_statements_public: e.target.checked})}
                  disabled={!canEdit}
                  className="h-4 w-4"
                />
                <Label htmlFor="financial_statements_public">{t('social_actor_assessment.field_financials_public')}</Label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="has_complaints_mechanism"
                  checked={assessmentData.has_complaints_mechanism}
                  onChange={(e) => setAssessmentData({...assessmentData, has_complaints_mechanism: e.target.checked})}
                  disabled={!canEdit}
                  className="h-4 w-4"
                />
                <Label htmlFor="has_complaints_mechanism">{t('social_actor_assessment.field_complaints_mechanism')}</Label>
              </div>

              <div>
                <Label>{t('social_actor_assessment.field_certifications_label')}</Label>
                <div className="text-sm text-gray-600 mb-2">
                  {assessmentData.certifications_held && assessmentData.certifications_held.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {assessmentData.certifications_held.map((cert, index) => (
                        <Badge key={index} variant="outline">{cert}</Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">{t('social_actor_assessment.no_certs')}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Investment Readiness Tab */}
        <TabsContent value="investment" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('social_actor_assessment.card_investment_title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="investment_ready"
                  checked={assessmentData.investment_ready}
                  onChange={(e) => setAssessmentData({...assessmentData, investment_ready: e.target.checked})}
                  disabled={!canEdit}
                  className="h-4 w-4"
                />
                <Label htmlFor="investment_ready">{t('social_actor_assessment.field_investment_ready')}</Label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="seeking_investment"
                  checked={assessmentData.seeking_investment}
                  onChange={(e) => setAssessmentData({...assessmentData, seeking_investment: e.target.checked})}
                  disabled={!canEdit}
                  className="h-4 w-4"
                />
                <Label htmlFor="seeking_investment">{t('social_actor_assessment.field_seeking_investment')}</Label>
              </div>

              {assessmentData.seeking_investment && (
                <>
                  <div>
                    <Label htmlFor="investment_amount_sought">{t('social_actor_assessment.field_investment_amount')}</Label>
                    <Input
                      id="investment_amount_sought"
                      type="number"
                      value={assessmentData.investment_amount_sought}
                      onChange={(e) => setAssessmentData({...assessmentData, investment_amount_sought: e.target.value})}
                      disabled={!canEdit}
                      placeholder={t('social_actor_assessment.field_investment_amount_placeholder')}
                    />
                  </div>

                  <div>
                    <Label htmlFor="investment_type_preferred">{t('social_actor_assessment.field_investment_type')}</Label>
                    <Select
                      value={assessmentData.investment_type_preferred}
                      onValueChange={(value) => setAssessmentData({...assessmentData, investment_type_preferred: value})}
                      disabled={!canEdit}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('social_actor_assessment.field_investment_type_placeholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="equity">{t('social_actor_assessment.type_equity')}</SelectItem>
                        <SelectItem value="debt">{t('social_actor_assessment.type_debt')}</SelectItem>
                        <SelectItem value="quasi-equity">{t('social_actor_assessment.type_quasi_equity')}</SelectItem>
                        <SelectItem value="grant">{t('social_actor_assessment.type_grant')}</SelectItem>
                        <SelectItem value="hybrid">{t('social_actor_assessment.type_hybrid')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="has_business_plan"
                  checked={assessmentData.has_business_plan}
                  onChange={(e) => setAssessmentData({...assessmentData, has_business_plan: e.target.checked})}
                  disabled={!canEdit}
                  className="h-4 w-4"
                />
                <Label htmlFor="has_business_plan">{t('social_actor_assessment.field_business_plan')}</Label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="has_financial_projections"
                  checked={assessmentData.has_financial_projections}
                  onChange={(e) => setAssessmentData({...assessmentData, has_financial_projections: e.target.checked})}
                  disabled={!canEdit}
                  className="h-4 w-4"
                />
                <Label htmlFor="has_financial_projections">{t('social_actor_assessment.field_financial_projections')}</Label>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {canEdit && (
        <div className="flex justify-end gap-2 pt-4">
          <Button onClick={handleSave} className="flex items-center gap-2">
            <Save className="h-4 w-4" />
            {t('social_actor_assessment.save_btn')}
          </Button>
          <Button 
            onClick={async () => {
              try {
                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/social-actors/${actor.id}/calculate-score`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                  }
                });
                const data = await response.json();
                if (response.ok) {
                  if (data.status === 'pending_review') {
                    toast.success(t('social_actor_assessment.toast_scores_pending', { score: data.scores.overall, defaultValue: 'Self-assessment of {{score}}% submitted. Pending PM/Admin approval — your published score will not change until approved.' }));
                  } else {
                    toast.success(t('social_actor_assessment.toast_scores_calculated', { score: data.scores.overall }));
                  }
                  // Refresh actor data in-place (no full page reload)
                  await refreshActor();
                } else {
                  toast.error(data?.detail || t('social_actor_assessment.toast_scores_failed'));
                }
              } catch (error) {
                console.error('Error calculating scores:', error);
                toast.error(t('social_actor_assessment.toast_scores_failed'));
              }
            }}
            variant="outline"
            className="flex items-center gap-2"
            data-testid="calculate-score-btn"
          >
            <TrendingUp className="h-4 w-4" />
            {t('social_actor_assessment.calculate_btn')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default SocialActorAssessment;
